// src/hooks/useGameEngine.js
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { createGameMachine } from "../game/engine/gameMachine";
import { generateCombosForSet } from "../game/utils/comboGenerator";

const useGameEngine = ({
  poses,
  actions,
  consumeAction,
  comboTimeFactor,
  mode,
  currentPhase,
  playPunchSound,
  playPopSound,
  playDodgeSound,
  playRestSound,
  playBeaten,
  position,
}) => {
  const gameMachine = useMemo(
    () =>
      createGameMachine({
        consumeAction,
        playPunchSound,
        playPopSound,
        playDodgeSound,
        playRestSound,
        playBeaten,
      }),
    [
      consumeAction,
      playPunchSound,
      playPopSound,
      playDodgeSound,
      playRestSound,
      playBeaten,
    ]
  );

  const send = useCallback(
    (event, payload) => {
      gameMachine.send(event, payload);
    },
    [gameMachine]
  );

  const [engineState, setEngineState] = useState({
    state: "idle",
    context: {},
  });

  const latestEngineState = useRef(engineState);
  const posesRef = useRef(poses);
  const actionsRef = useRef(actions);
  const positionRef = useRef(position);
  const comboSetRef = useRef(null); // Ref to store the current set's combos
  const comboTimerRef = useRef(null); // Ref for the combo timer
  const dodgeTimerRef = useRef(null); // Ref for the dodge timer

  useEffect(() => {
    posesRef.current = poses;
    actionsRef.current = actions;
    positionRef.current = position;
  }, [poses, actions, position]);

  useEffect(() => {
    const unsubscribe = gameMachine.subscribe((newState) => {
      latestEngineState.current = newState;
    });

    let gameLoopId;
    let renderLoopId;

    const gameLoop = () => {
      if (latestEngineState.current.state === "playing") {
        const currentPoses = posesRef.current;
        const currentActions = actionsRef.current;
        const currentPosition = positionRef.current;
        if (currentPoses && currentActions) {
          send("POSE_UPDATE", {
            poses: currentPoses,
            actions: currentActions,
            position: currentPosition,
          });
        }
      }
      gameLoopId = requestAnimationFrame(gameLoop);
    };

    const renderLoop = () => {
      if (latestEngineState.current !== engineState) {
        setEngineState(latestEngineState.current);
      }
    };

    if (engineState.state !== "complete") {
      gameLoopId = requestAnimationFrame(gameLoop);
      renderLoopId = setInterval(renderLoop, 33); // ~30fps
    }

    return () => {
      unsubscribe();
      if (gameLoopId) cancelAnimationFrame(gameLoopId);
      if (renderLoopId) clearInterval(renderLoopId);
      if (comboTimerRef.current) clearTimeout(comboTimerRef.current);
      if (dodgeTimerRef.current) clearTimeout(dodgeTimerRef.current);
    };
  }, [gameMachine, send, engineState.state]);

  // Effect for handling combo generation and setting
  useEffect(() => {
    if (comboTimeFactor) {
      const newCombos = generateCombosForSet(mode);
      comboSetRef.current = newCombos; // Keep a ref to the combos
      send("SET_COMBOS", newCombos);
    }
  }, [send, comboTimeFactor, mode]); // Removed gameMachine dependency

  // Effect to start the game and load the first combo
  useEffect(() => {
    if (engineState.state === "playing" && comboTimeFactor) {
      send("START");
      send("SET_NEXT_COMBO");
    }
  }, [engineState.state, comboTimeFactor, send]);

  // Main game logic for scheduling moves and combos
  useEffect(() => {
    // Ensure we are in the playing state and have a combo
    if (engineState.state !== "playing" || !engineState.context.currentCombo) {
      return;
    }

    const { currentCombo, currentPunchIndex } = engineState.context;
    const currentMove = currentCombo[currentPunchIndex];

    if (comboTimerRef.current) clearTimeout(comboTimerRef.current);
    if (dodgeTimerRef.current) clearTimeout(dodgeTimerRef.current);

    if (currentMove) {
      // If there's a current move, handle it
      if (currentMove.type === "dodge") {
        send("DODGE_START");
        console.log("DODGE_START");
        const anticipationTime = comboTimeFactor * 200; // * (Math.random() + 0.5)
        dodgeTimerRef.current = setTimeout(() => {
          console.log("DODGE_IMPACT");
          send("DODGE_IMPACT");
          dodgeTimerRef.current = setTimeout(() => {
            console.log("DODGE_SUCCESS");
            send("DODGE_SUCCESS");
          }, comboTimeFactor * 1000);
        }, anticipationTime);
      }
      // For punches, we wait for user input, so no timer is needed here.
    } else {
      // If there's no current move, the combo is complete.
      const timeToNextCombo =
        (currentCombo.length * comboTimeFactor || 2) * 1000;
      comboTimerRef.current = setTimeout(() => {
        send("SET_NEXT_COMBO");
      }, timeToNextCombo);
    }

    return () => {
      if (comboTimerRef.current) clearTimeout(comboTimerRef.current);
      if (dodgeTimerRef.current) clearTimeout(dodgeTimerRef.current);
    };
  }, [
    engineState.state,
    engineState.context.currentCombo,
    engineState.context.currentPunchIndex,
    comboTimeFactor,
    send,
  ]);

  return {
    gameState: engineState.state,
    gameContext: engineState.context,
    send,
  };
};

export default useGameEngine;
